/** Module G — Constraint cell rasterizer (§6 of Features/Set5.md). Consumes V3 terrain
 *  features + `PoissonSolverConfig`, fills an `AMRGrid` with `CellType` + BC values (§6.1:
 *  Barrier > Dirichlet > Neumann > Free). MapObject collection: {@link collectRasterFeatures}. */

import {
	type AMRGrid, type QuadNode,
	CellType, Face,
	NEU_E, NEU_N, NEU_W, NEU_S,
	oppositeFace, FACE_DX, FACE_DY,
	cellSizeAtLevel, levelForCellSize,
	refineNode, leavesInRect, collectLeavesInRect, findLeafContaining,
} from "../AMRGrid.js";
import type {PoissonSolverConfig} from "../../IsolineTypes.js";
import {type VetoSignal, isVetoSignal, SolveAbortedError} from "../Solve/Solve_Common.js";
import {CliffFeature, ClosedRingFeature, OpenCurveFeature, Pt, RasterFeature, SpotHeightFeature} from "./ConstraintRasterizer/RasterFeatures_Pure.js";
import {MapObject_ForTerrain} from "./ConstraintRasterizer/MapObject_ForTerrain.js";
import {Assert} from "js-vextensions";

// ============================================================
// Geometry helpers
// ============================================================

/** Point-in-polygon (ray-casting). Returns true if (x, y) lies inside the closed polygon
 *  `pts` (last point implicitly connects to first). Mirrors the algorithm in
 *  `IsolineEvaluator.ts` / `PolygonUtils.ts` but operates on `{x, y}` points. */
export function pointInPolygon(x: number, y: number, pts: ReadonlyArray<Pt>): boolean {
	let inside = false;
	for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
		const xi = pts[i].x, yi = pts[i].y;
		const xj = pts[j].x, yj = pts[j].y;
		const intersect = ((yi > y) !== (yj > y))
			&& (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
		if (intersect) inside = !inside;
	}
	return inside;
}

/** Segment-vs-AABB intersection test (slab method). Returns true if segment a→b intersects
 *  the AABB [minX,minY,maxX,maxY] inclusive. Used to detect "rim" cells the tessellated
 *  polyline passes through. */
export function segmentIntersectsAABB(
	ax: number, ay: number, bx: number, by: number,
	minX: number, minY: number, maxX: number, maxY: number,
): boolean {
	// Liang-Barsky parametric clipping.
	const dx = bx - ax, dy = by - ay;
	let t0 = 0, t1 = 1;
	const p = [-dx, dx, -dy, dy];
	const q = [ax - minX, maxX - ax, ay - minY, maxY - ay];
	for (let i = 0; i < 4; i++) {
		if (p[i] === 0) {
			// Parallel to this slab; segment must be inside the slab's half-plane.
			if (q[i] < 0) return false;
		} else {
			const t = q[i] / p[i];
			if (p[i] < 0) {
				if (t > t1) return false;
				if (t > t0) t0 = t;
			} else {
				if (t < t0) return false;
				if (t < t1) t1 = t;
			}
		}
	}
	return true;
}

/** Quick bounding-box of a point list. */
function bboxOf(pts: ReadonlyArray<Pt>): {minX: number; minY: number; maxX: number; maxY: number} {
	let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
	for (const p of pts) {
		if (p.x < minX) minX = p.x;
		if (p.y < minY) minY = p.y;
		if (p.x > maxX) maxX = p.x;
		if (p.y > maxY) maxY = p.y;
	}
	return {minX, minY, maxX, maxY};
}

/** Distance from point P to segment AB. */
function distPointToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
	const dx = bx - ax, dy = by - ay;
	const lenSq = dx * dx + dy * dy;
	let t = lenSq > 0 ? ((px - ax) * dx + (py - ay) * dy) / lenSq : 0;
	t = Math.max(0, Math.min(1, t));
	const cx = ax + t * dx, cy = ay + t * dy;
	const ex = px - cx, ey = py - cy;
	return Math.sqrt(ex * ex + ey * ey);
}

// ============================================================
// RasterFeature — solver-friendly intermediate representation
// ============================================================


// ============================================================
// Rasterization result + diagnostics
// ============================================================

export interface RasterizeResult {
	/** Cell counts by type (indexed by CellType numeric value). */
	counts: {free: number; dirichlet: number; neumann: number; barrier: number; outer: number};
	/** Same-cell, same-resolution Dirichlet conflicts detected (user errors). */
	dirichletConflicts: number;
	/** Refinement level actually reached (deepest leaf). */
	maxLevelReached: number;
}

// ============================================================
// Refinement helpers
// ============================================================

/** Refine every leaf whose AABB intersects `rect` down to `targetLevel`.
 *  Leaves already at/beyond `targetLevel` are left alone; the polygon/polyline/disk
 *  refiners all reduce to this workhorse. */
export function refineLeavesIntersectingRect(
	grid: AMRGrid,
	minX: number, minY: number, maxX: number, maxY: number,
	targetLevel: number,
): void {
	// Stack-based descent; avoid recursion to keep deep grids cheap.
	const stack: number[] = [0];
	while (stack.length > 0) {
		const nodeIdx = stack.pop()!;
		const n = grid.nodes[nodeIdx];
		// AABB rejection.
		if (n.maxX < minX || n.minX > maxX || n.maxY < minY || n.minY > maxY) continue;
		if (n.firstChild < 0) {
			if (n.level < targetLevel) {
				refineNode(grid, nodeIdx);
				// Children are now present; push them for further descent.
				for (let q = 0; q < 4; q++) stack.push(n.firstChild + q);
			}
		} else {
			for (let q = 0; q < 4; q++) stack.push(n.firstChild + q);
		}
	}
}

// ============================================================
// Cell-writer (applies §6.1 precedence)
// ============================================================

/** Precedence ranks. Higher wins. Barrier > Dirichlet > SoftPin > Neumann > Free.
 *  (Neumann is a face property, not a cell type, so the "Neumann cell type" path is rare —
 *  we keep the rank for completeness.) */
const RANK_FREE = 0, RANK_NEUMANN = 1, RANK_SOFTPIN = 2, RANK_DIRICHLET = 3, RANK_BARRIER = 4;

function typeRank(t: CellType): number {
	switch (t) {
		case CellType.Free: return RANK_FREE;
		case CellType.Neumann: return RANK_NEUMANN;
		case CellType.SoftPin: return RANK_SOFTPIN;
		case CellType.Dirichlet: return RANK_DIRICHLET;
		case CellType.Barrier: return RANK_BARRIER;
	}
}

/** Write a Dirichlet cell value. Honors precedence: if the cell is already Barrier, this is
 *  a no-op (barrier wins). If already Dirichlet at a different value, record a conflict
 *  and let the caller decide (by default higher-resolution/later features overwrite). */
function writeDirichlet(grid: AMRGrid, cellIdx: number, value: number, stats: RasterizeResult, dominance: number = 0): boolean {
	const cells = grid.cells;
	const cur = cells.type[cellIdx] as CellType;
	if (cur === CellType.Barrier) return false; // barrier wins
	// **2026-08-02 — dominance block.** A write to a cell pinned by a strictly-higher-
	// dominance feature is blocked (equal/lower = no block → default-0 maps keep
	// later-writer-wins); false → caller reroutes via `fallbackPin`.
	if ((cur === CellType.Dirichlet || cur === CellType.SoftPin) && cells.dominance[cellIdx] > dominance) {
		return false;
	}
	if (cur === CellType.Dirichlet && cells.bcValue[cellIdx] !== value) {
		stats.dirichletConflicts++;
	}
	cells.type[cellIdx] = CellType.Dirichlet;
	cells.bcValue[cellIdx] = value;
	cells.dominance[cellIdx] = dominance;
	return true;
}

/** Write a Barrier cell. Always wins over non-barrier types. */
function writeBarrier(grid: AMRGrid, cellIdx: number): void {
	grid.cells.type[cellIdx] = CellType.Barrier;
}

/** **2026-07-30/31 — `pinType: "soft"` (was `pinShape: "rim_soft"`).** Mark `cellIdx` as
 *  SoftPin (penalty `k·(H − target)`); `stiffness` is pre-scaled k (caller scales by 1/Δ² so
 *  k=1 ≈ one Laplacian neighbour). Loses to Barrier, beats Dirichlet; false when blocked. */
function writeSoftPin(grid: AMRGrid, cellIdx: number, target: number, stiffness: number, dominance: number = 0): boolean {
	const cells = grid.cells;
	const cur = cells.type[cellIdx] as CellType;
	if (cur === CellType.Barrier) return false; // barrier wins
	// **2026-08-02 — dominance block** (same rule as `writeDirichlet`).
	if ((cur === CellType.Dirichlet || cur === CellType.SoftPin) && cells.dominance[cellIdx] > dominance) {
		return false;
	}
	cells.type[cellIdx] = CellType.SoftPin;
	cells.softPinTarget[cellIdx] = target;
	cells.softPinStiffness[cellIdx] = stiffness;
	cells.dominance[cellIdx] = dominance;
	return true;
}

/** Set a Neumann face pin on `cellIdx`'s `face`. Does not change the cell's `type` (a cell
 *  can be Free or Dirichlet and still carry Neumann faces per §6.5). Stores g0 in the
 *  face's outward-normal direction; the solver applies `NEU_SIGN[face]` when assembling. */
function writeNeumannFace(grid: AMRGrid, cellIdx: number, face: Face, g0: number): void {
	const cells = grid.cells;
	cells.neumannFaces[cellIdx] |= (1 << face);
	cells.neumannG0[cellIdx * 4 + face] = g0;
}

// ============================================================
// 2026-08-02 — Fallback-pin search (reroute a blocked pin to the closest unblocked cell)
// ============================================================

/** **2026-08-02 — fallback pin (closed-ring interior).** When a closed-ring write to
 *  `blockedNodeIdx` is blocked by a strictly-higher-dominance pin, reroute to the nearest
 *  leaf INSIDE `poly` with lower dominance; no candidate → pin dropped. `writer` stamps it. */
/** **2026-08-05 — `relocatedWriter` (band-fallback): band-mode callers re-seed their BFS from the relocated cell;
 * the relocation IS the pin (band writes at full k; c/f after); `writer` cell-stamp skipped. */
function fallbackPinInterior(
	grid: AMRGrid,
	blockedNodeIdx: number,
	poly: ReadonlyArray<Pt>,
	targetLevel: number,
	writer: (worldPos: {x: number; y: number}, nodeIdx: number)=>void,
	cOverride: number | undefined,
	fOverride: number | undefined,
	relocatedWriter?: (worldPos: {x: number; y: number}, nodeIdx: number)=>void,
): void {
	const cells = grid.cells;
	const blocked = grid.nodes[blockedNodeIdx];
	const blockedCellIdx = blocked.cellIndex;
	// Eligibility test: we only have the blocker's dominance (which is > ours), and ours <
	// blocker's ⟹ `dominance[ci] < blockerDom` is correct + safe: strictly-less is ≤ ours,
	// equal belongs to the blocker. Free/Neumann (dominance 0) are eligible.
	const blockerDom = cells.dominance[blockedCellIdx];

	// BFS state.  Per-cell metres-distance from the blocked cell centre; -1 = unvisited.
	const dist = new Float64Array(cells.count).fill(-1);
	const nodeForCell = new Int32Array(cells.count).fill(-1);
	const blockedCenter = {
		x: (blocked.minX + blocked.maxX) * 0.5,
		y: (blocked.minY + blocked.maxY) * 0.5,
	};
	// Seed: the blocked cell's interior neighbours (NOT the blocked cell itself — it's
	// blocked; we want the closest OTHER cell).  Discovered via the face-midpoint pattern.
	const halfCell = (nodeIdx: number): number=>{
		const n = grid.nodes[nodeIdx];
		return (n.maxX - n.minX) * 0.5;
	};
	const root = grid.nodes[0];
	const neighborsOf = (nodeIdx: number): number[]=>{
		const node = grid.nodes[nodeIdx];
		const sz = node.maxX - node.minX;
		const eps = sz * 1e-3;
		const out: number[] = [];
		const offsets = sz === 0 ? [0.5] : [0.25, 0.75];
		for (let dir = 0; dir < 4; dir++) {
			const dx = FACE_DX[dir], dy = FACE_DY[dir];
			for (const off of offsets) {
				let tx: number, ty: number;
				if (dx !== 0) { tx = (dx > 0 ? node.maxX : node.minX) + dx * eps; ty = node.minY + sz * off; }
				else          { tx = node.minX + sz * off; ty = (dy > 0 ? node.maxY : node.minY) + dy * eps; }
				if (tx < root.minX || tx > root.maxX || ty < root.minY || ty > root.maxY) continue;
				const nb = findLeafContaining(grid, tx, ty);
				if (nb !== nodeIdx && !out.includes(nb)) out.push(nb);
			}
		}
		return out;
	};

	const queue: number[] = [];
	for (const nb of neighborsOf(blockedNodeIdx)) {
		const nci = grid.nodes[nb].cellIndex;
		if (nci < 0) continue;
		// Only retreat INTO the polygon (interior cells).  Rim / exterior neighbours are
		// not candidates — the relocated pin must stay inside the ring's true footprint.
		const nn = grid.nodes[nb];
		const nx = (nn.minX + nn.maxX) * 0.5, ny = (nn.minY + nn.maxY) * 0.5;
		if (!pointInPolygon(nx, ny, poly)) continue;
		// Only iterate leaves at the feature's target resolution (coarser fringe leaves are
		// outside the ring's refined footprint; finer leaves don't exist in the uniform-level
		// ring pad).  Matches `rasterizeClosedRing`'s `if (node.level < targetLevel) return`.
		if (nn.level < targetLevel) continue;
		const edge = halfCell(blockedNodeIdx) + halfCell(nb);
		if (dist[nci] < 0 || edge < dist[nci]) {
			dist[nci] = edge;
			nodeForCell[nci] = nb;
			queue.push(nb);
		}
	}

	// BFS — relax-if-shorter (Dijkstra-with-FIFO; uniform-level grid → adequate).
	let chosen: number = -1;
	while (queue.length > 0) {
		const nodeIdx = queue.shift()!;
		const ci = grid.nodes[nodeIdx].cellIndex;
		const d = dist[ci];
		// Eligible: dominance strictly less than the blocker's (see derivation above) AND not
		// Barrier (Barrier absolute-wins — relocating onto one would be dropped by the
		// writer's short-circuit; skipping it keeps the BFS exploring past it).
		const t = cells.type[ci] as CellType;
		const eligible = (t !== CellType.Barrier) && (cells.dominance[ci] < blockerDom);
		if (eligible) { chosen = nodeIdx; break; }
		for (const nb of neighborsOf(nodeIdx)) {
			const nci = grid.nodes[nb].cellIndex;
			if (nci < 0) continue;
			const nn = grid.nodes[nb];
			const nx = (nn.minX + nn.maxX) * 0.5, ny = (nn.minY + nn.maxY) * 0.5;
			if (!pointInPolygon(nx, ny, poly)) continue;
			if (nn.level < targetLevel) continue;
			const edge = d + halfCell(nodeIdx) + halfCell(nb);
			if (dist[nci] < 0 || edge < dist[nci]) {
				dist[nci] = edge;
				nodeForCell[nci] = nb;
				queue.push(nb);
			}
		}
	}

	if (chosen < 0) return; // no unblocked interior cell found → drop the pin
	const n = grid.nodes[chosen];
	const wpos = {x: (n.minX + n.maxX) * 0.5, y: (n.minY + n.maxY) * 0.5};
	if (relocatedWriter != null) {
		// Band-fallback: the relocation IS the pin — hand the moved cell to the band-mode
		// caller (which re-seeds its BFS there); no direct cell stamp here (the band pass
		// writes at full k from the relocated seed + applies c/f after it runs).
		relocatedWriter(wpos, chosen);
		return;
	}
	writer(wpos, chosen);
	if (cOverride != null) cells.c[n.cellIndex] = cOverride;
	if (fOverride != null) cells.f[n.cellIndex] = fOverride;
}

/** **2026-08-02 — fallback pin (rect-bounded; open-curve tube / spot-height disk).**
 *  Same nearest-unblocked-cell BFS as `fallbackPinInterior` but over a world-space rect,
 *  optionally gated by `withinRegion(nodeIdx)`. For features with no polygon to retreat "inside". */
function fallbackPinInRect(
	grid: AMRGrid,
	blockedNodeIdx: number,
	searchRect: {minX: number; minY: number; maxX: number; maxY: number},
	withinRegion: ((nodeIdx: number)=>boolean) | undefined,
	targetLevel: number,
	writer: (worldPos: {x: number; y: number}, nodeIdx: number)=>void,
): void {
	const cells = grid.cells;
	const blocked = grid.nodes[blockedNodeIdx];
	const blockedCellIdx = blocked.cellIndex;
	const blockerDom = cells.dominance[blockedCellIdx];
	const halfCell = (nodeIdx: number): number=>{
		const n = grid.nodes[nodeIdx];
		return (n.maxX - n.minX) * 0.5;
	};
	const root = grid.nodes[0];
	const neighborsOf = (nodeIdx: number): number[]=>{
		const node = grid.nodes[nodeIdx];
		const sz = node.maxX - node.minX;
		const eps = sz * 1e-3;
		const out: number[] = [];
		const offsets = sz === 0 ? [0.5] : [0.25, 0.75];
		for (let dir = 0; dir < 4; dir++) {
			const dx = FACE_DX[dir], dy = FACE_DY[dir];
			for (const off of offsets) {
				let tx: number, ty: number;
				if (dx !== 0) { tx = (dx > 0 ? node.maxX : node.minX) + dx * eps; ty = node.minY + sz * off; }
				else          { tx = node.minX + sz * off; ty = (dy > 0 ? node.maxY : node.minY) + dy * eps; }
				if (tx < root.minX || tx > root.maxX || ty < root.minY || ty > root.maxY) continue;
				const nb = findLeafContaining(grid, tx, ty);
				if (nb !== nodeIdx && !out.includes(nb)) out.push(nb);
			}
		}
		return out;
	};
	const inRect = (nodeIdx: number): boolean=>{
		const n = grid.nodes[nodeIdx];
		return n.maxX >= searchRect.minX && n.minX <= searchRect.maxX
			&& n.maxY >= searchRect.minY && n.minY <= searchRect.maxY;
	};
	const accept = (nodeIdx: number): boolean=>{
		const n = grid.nodes[nodeIdx];
		if (n.level < targetLevel) return false;
		if (!inRect(nodeIdx)) return false;
		if (withinRegion != null && !withinRegion(nodeIdx)) return false;
		return true;
	};

	const dist = new Float64Array(cells.count).fill(-1);
	const queue: number[] = [];
	for (const nb of neighborsOf(blockedNodeIdx)) {
		const nci = grid.nodes[nb].cellIndex;
		if (nci < 0 || !accept(nb)) continue;
		const edge = halfCell(blockedNodeIdx) + halfCell(nb);
		if (dist[nci] < 0 || edge < dist[nci]) { dist[nci] = edge; queue.push(nb); }
	}
	let chosen = -1;
	while (queue.length > 0) {
		const nodeIdx = queue.shift()!;
		const ci = grid.nodes[nodeIdx].cellIndex;
		const d = dist[ci];
		const t = cells.type[ci] as CellType;
		if (t !== CellType.Barrier && cells.dominance[ci] < blockerDom) { chosen = nodeIdx; break; }
		for (const nb of neighborsOf(nodeIdx)) {
			const nci = grid.nodes[nb].cellIndex;
			if (nci < 0 || !accept(nb)) continue;
			const edge = d + halfCell(nodeIdx) + halfCell(nb);
			if (dist[nci] < 0 || edge < dist[nci]) { dist[nci] = edge; queue.push(nb); }
		}
	}
	if (chosen < 0) return;
	const n = grid.nodes[chosen];
	writer({x: (n.minX + n.maxX) * 0.5, y: (n.minY + n.maxY) * 0.5}, chosen);
}

// ============================================================
// Per-feature rasterizers
// ============================================================

/** §6.2 — Isoline ring rasterization. Refines the ring footprint to `cellSize`;
 *  `pinInterior` → interior+rim cells Dirichlet at `feat.elevation` (+ outward Neumann rim
 *  face if enabled); `!pinInterior` → rim-only Dirichlet; `noFlux` → rim Barrier flux wall. */
/** 2026-08-03 rapid-zoom-queue fix — extracted the `rasterizeClosedRing` per-leaf body into
 *  this helper so it runs via BOTH the sync `leavesInRect` callback (back-compat, byte-
 *  identical) and an async array path (with a `VetoSignal` → `await maybeYield()`). */
function rasterizeClosedRing_leafVisit(
	grid: AMRGrid, nodeIdx: number, feat: ClosedRingFeature,
	stats: RasterizeResult, targetLevel: number, poly: Pt[],
	cOverride: number | undefined, fOverride: number | undefined,
	writeInteriorPin: boolean, useRimElevations: boolean, rimElevations: number[] | undefined,
	doNeumann: boolean, g0Signed: number,
	doSoftPin: boolean, softPinK: number, doBand: boolean,
	featDominance: number, doFallback: boolean,
	rimNodeIndices: number[],
): void {
	const node = grid.nodes[nodeIdx];
	// Only classify leaves at the target resolution (or finer). Coarser leaves at the
	// patch fringe that didn't get refined stay Free — they're outside the ring footprint.
	if (node.level < targetLevel) return;
	const cellIdx = node.cellIndex;
	const cx = (node.minX + node.maxX) * 0.5;
	const cy = (node.minY + node.maxY) * 0.5;
	const inside = pointInPolygon(cx, cy, poly);
	// Rim = the tessellated polyline passes through this cell's AABB.
	// **The `isRim` sweep MUST run for BOTH `inside` and `!inside` cells** — the prior
	// `if (!inside)` gate leaked the interior field through rim cells on the interior half.
	let isRim = false;
	for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
		if (segmentIntersectsAABB(poly[j].x, poly[j].y, poly[i].x, poly[i].y,
			node.minX, node.minY, node.maxX, node.maxY)) {
			isRim = true;
			break;
		}
	}
	const pinThisCell = (inside && writeInteriorPin) || isRim;
	if (pinThisCell) {
		// noFlux: turn the rim cell into a Barrier (unconditionally on rim cells when
		// noFlux). The subsequent writeDirichlet becomes a no-op (prior-Barrier
		// short-circuit) — the pin's elevation is solver context, not propagated.
		if (feat.noFlux && isRim) writeBarrier(grid, cellIdx);
		const elev = isRim && useRimElevations
			? sampleElevationAlongPolyline(cx, cy, poly, rimElevations!)
			: feat.elevation;
		// **`pinType: "soft"` (was `pinShape: "rim_soft"`).** `doSoftPin` → pin cells are
		// SoftPin: `pinInterior=true` → interior+rim at uniform k (plateau); false → rim only
		// (guider). `doBand` → band pass writes the rim (seed here; ramp=1 at d=0).
		const isSoftPinCell = doSoftPin && (isRim || (inside && writeInteriorPin));
		if (isSoftPinCell) {
			if (isRim && doBand) {
				// Band mode: the band pass is the authoritative rim writer — collect the seed
				// here. (Interior soft-plateau cells fall through to the write below — the
				// outward-only band doesn't touch interior cells.)

				// **2026-08-05 — band + fallback: probe each seed with a full-k write (idempotent
				// with the band's ramp-1 re-write); blocked → relocate the seed inward.
				const wroteSeed = writeSoftPin(grid, cellIdx, elev, softPinK, featDominance);
				if (wroteSeed) {
					rimNodeIndices.push(nodeIdx);
				} else if (doFallback && inside) {
					// Blocked rim seed → relocate the band source to the closest unblocked interior
					// leaf (stays inside the ring's polygon); the band BFS starts from the moved cell.
					fallbackPinInterior(grid, nodeIdx, poly, targetLevel,
						(ipos, irel)=>rimNodeIndices.push(irel),
						cOverride, fOverride,
						(ipos, irel)=>rimNodeIndices.push(irel));
				}
			} else {
				// Non-band rim (1-cell soft pin), OR a soft-plateau interior cell (in band
				// or non-band mode — interior cells are always written here at uniform k).
				const wrote = writeSoftPin(grid, cellIdx, elev, softPinK, featDominance);
				if (wrote) {
					if (cOverride != null) grid.cells.c[cellIdx] = cOverride;
					if (fOverride != null) grid.cells.f[cellIdx] = fOverride;
				} else if (doFallback && inside) {
					// Blocked by a higher-dominance pin → reroute to the closest unblocked
					// interior leaf (only meaningful for interior-cell writes — a blocked rim
					// cell has no "inside" to retreat into → dropped).
					fallbackPinInterior(grid, nodeIdx, poly, targetLevel,
						(ipos, irel)=>writeSoftPin(grid, grid.nodes[irel].cellIndex,
							useRimElevations && rimElevations != null
								? sampleElevationAlongPolyline(ipos.x, ipos.y, poly, rimElevations)
								: elev,
							softPinK, featDominance),
						cOverride, fOverride);
				}
			}
		} else {
			const wrote = writeDirichlet(grid, cellIdx, elev, stats, featDominance);
			if (wrote) {
				if (cOverride != null) grid.cells.c[cellIdx] = cOverride;
				if (fOverride != null) grid.cells.f[cellIdx] = fOverride;
			} else if (doFallback && inside) {
				// Blocked by a higher-dominance pin → reroute the Dirichlet pin to the closest
				// unblocked interior leaf inside this ring's polygon (e.g. an inner ring's pin
				// blocked by an outer higher-dominance ring). Rim-only rings (`inside` false) drop.
				fallbackPinInterior(grid, nodeIdx, poly, targetLevel,
					(ipos, irel)=>writeDirichlet(grid, grid.nodes[irel].cellIndex,
						isRim && useRimElevations && rimElevations != null
							? sampleElevationAlongPolyline(ipos.x, ipos.y, poly, rimElevations)
							: elev,
						stats, featDominance),
					cOverride, fOverride);
			}
		}
	}
	// Neumann rim pin (§6.5): dominant outward face from the local curve normal.
	// Interior cells are Dirichlet (no Neumann needed).
	if (doNeumann && isRim) {
		const face = dominantOutwardFace(grid, node, poly);
		if (face >= 0) writeNeumannFace(grid, cellIdx, face as Face, g0Signed);
	}
}

async function rasterizeClosedRing(grid: AMRGrid, feat: ClosedRingFeature, cfg: PoissonSolverConfig, stats: RasterizeResult, signal?: VetoSignal): Promise<void> {
	const targetCellSize = feat.cellSize ?? cfg.defaultConstraintCellSize;
	const targetLevel = levelForCellSize(grid.rootSize, targetCellSize, grid.maxDepth);	// **2026-07-30 — `softPinBandWidth` (Strategy A).** > 0 spreads the soft-pin penalty
	// across a band of width W (m): stiffness ramps `k·cos(π·d/(2W))` (1 at rim → 0 at
	// edge) → smooth, seam-free field. Refine pad widens to `bandWidth + one cell`.
	const softPinBandWidth = feat.softPin === true ? (feat.softPinBandWidth ?? 0) : 0;
	const doBand = softPinBandWidth > 0;

	// 1) Refine the ring's bbox (padded by one target cell; by `bandWidth + one cell`
	//    when the soft-pin band mode is active so band cells exist).
	const bb = bboxOf(feat.polyline);
	const refinePad = cellSizeAtLevel(grid, targetLevel) + (doBand ? softPinBandWidth : 0);
	refineLeavesIntersectingRect(
		grid,
		bb.minX - refinePad, bb.minY - refinePad, bb.maxX + refinePad, bb.maxY + refinePad,
		targetLevel,
	);

	// 2) Walk refined leaves in the bbox; classify interior / rim / free.
	const poly = feat.polyline;
	const cOverride = feat.regionC, fOverride = feat.regionF;
	const doNeumann = cfg.enableNeumann && feat.crossingSlope != null && feat.crossingSlope > 0;
	const g0Base = feat.crossingSlope ?? 0;
	const interiorUp = (feat.orientation ?? "interiorUp") === "interiorUp";
	// g0 in the outward-normal direction: interiorUp ⟹ field decreases outward ⟹ g0 < 0.
	const g0Signed = interiorUp ? -g0Base : g0Base;
	// pinInterior=false → rim-only Dirichlet/Neumann (mountain-ring ridge). noFlux → rim
	// cells are Barrier: writeBarrier runs BEFORE the Dirichlet pin (in rasterizeConstraints
	// / the leaf visit), so writeDirichlet's prior-Barrier short-circuit makes it a no-op.
	const writeInteriorPin = feat.pinInterior;
	const rimElevations = feat.rimElevations;
	const useRimElevations = !feat.pinInterior && rimElevations != null && rimElevations.length === poly.length;

	// **2026-07-30/31 — `pinType: "soft"` (was `pinShape: "rim_soft"`).** `feat.softPin` →
	// pin cells are SoftPin (penalty `k·(H − target)`, gradient free); k pre-scaled by 1/Δ² so
	// stiffness 1.0 ≈ one Laplacian neighbour. `pinInterior` → interior+rim (plateau) / rim only (guider).
	const doSoftPin = feat.softPin === true;
	// Δ from the cell size at targetLevel (all rim cells share it — the leaf-visit gate
	// returns below targetLevel).
	const softPinDelta = cellSizeAtLevel(grid, targetLevel);
	const softPinInvDelta2 = 1 / (softPinDelta * softPinDelta);
	const softPinK = (feat.softPinStiffness ?? 1) * softPinInvDelta2;

	// **2026-08-02 — pin dominance + fallback.** `featDominance` blocks later lower-
	// dominance pins; `doFallback` reroutes a blocked pin to the nearest unblocked leaf
	// inside this ring's polygon. Default 0 = no blocking (existing maps unchanged).
	const featDominance = feat.dominance ?? 0;
	const doFallback = feat.fallbackPin === true;

	// Collect rim node indices during the sweep for the band pass's BFS seeds (only used
	// when `doSoftPin && doBand`; collecting always avoids a second pass).
	const rimNodeIndices: number[] = [];

	// 2026-08-03 rapid-zoom-queue fix — collect bbox leaves into an array first (the
	// callback API can't `await` between leaves); the async loop below yields every 4096
	// leaves (≤1 setTimeout(0) per ~100ms throttle window).
	const leavesInBBox = signal != null
		? collectLeavesInRect(grid, bb.minX - refinePad, bb.minY - refinePad, bb.maxX + refinePad, bb.maxY + refinePad)
		: [];
	if (signal != null) {
		// Async iterated-array path with batched yield checkpoints (only when a VetoSignal
		// is present; the sync `leavesInRect` callback path below is the back-compat fast
		// path for test-lane callers that omit `signal`).
		for (let li = 0; li < leavesInBBox.length; li++) {
			const nodeIdx = leavesInBBox[li];
			if ((li & 4095) === 0) {
				if (signal && isVetoSignal(signal) && signal.aborted) throw new SolveAbortedError();
				if (signal && isVetoSignal(signal)) await signal.maybeYield();
				if (signal && isVetoSignal(signal) && signal.aborted) throw new SolveAbortedError();
			}
			rasterizeClosedRing_leafVisit(grid, nodeIdx, feat, stats, targetLevel, poly,
				cOverride, fOverride, writeInteriorPin, useRimElevations, rimElevations,
				doNeumann, g0Signed, doSoftPin, softPinK, doBand, featDominance, doFallback,
				rimNodeIndices);
		}
	} else {
		// Synchronous back-compat path (the original `leavesInRect` callback) —
		// byte-identical to the pre-fix behaviour for callers that omit `signal`.
		leavesInRect(grid, bb.minX - refinePad, bb.minY - refinePad, bb.maxX + refinePad, bb.maxY + refinePad, (nodeIdx)=>{
			rasterizeClosedRing_leafVisit(grid, nodeIdx, feat, stats, targetLevel, poly,
				cOverride, fOverride, writeInteriorPin, useRimElevations, rimElevations,
				doNeumann, g0Signed, doSoftPin, softPinK, doBand, featDominance, doFallback,
				rimNodeIndices);
		});
	}

	// **2026-07-30/31 — `softPinBandWidth` band-spread pass (Strategy A).** Multi-source BFS
	// from the rim outward up to `bandWidth` m, writing `k·cos(π·d/(2W))` SoftPin (smooth,
	// seam-free ramp; see `Set7_SmootherElevationPins.md`). Direction: "both" (rim-guider) / "outward" (soft-plateau).
	if (doSoftPin && doBand) {
		spreadSoftPinBand(
			grid, targetLevel, poly,
			rimNodeIndices, rimElevations, feat.elevation, useRimElevations,
			softPinK, softPinBandWidth,
			feat.softPinBandDirection ?? "both",
			cOverride, fOverride,
			featDominance,
			doFallback, // 2026-08-05 — reroute any seed still blocked at band-write time
		);
	}
}

/** **2026-07-30/31 — `softPinBandWidth` band-spread pass (Strategy A).** Multi-source BFS
 *  from the rim (seeds) outward up to `bandWidth` m, writing `k·cos(π·d/(2W))` SoftPin
 *  (1 at rim → 0 at band edge). Pull grows with W (lower `softPinStiffness` when widening). */
/** Writes rim cells FIRST (d=0, ramp=1, full k), then BFS outward — the AUTHORITATIVE
 *  soft-pin writer for RIM cells in band mode (the leaves loop only collects seeds).
 *  SoftPin wins over Free/Neumann but NOT Barrier/Dirichlet (hard pins are kept). */
/** **2026-07-31 — `bandDirection`:** which side of the rim the BFS ramps. `"both"`
 *  (soft-guider `pinShape:"rim"`) ramps inward+outward; `"outward"` (soft-plateau
 *  `pinShape:"interior"`) ramps exterior-only — refuses cells whose center is `inside`. */
/** **2026-08-05 — `doFallback` (band-fallback).** Seeds blocked at the rim are already
 *  relocated by the leaves sweep; this covers cells still blocked at band-write time — reroute
 *  via `fallbackPinInRect` (band footprint) at full k, else skip (higher-dominance owner governs). */
function spreadSoftPinBand(
	grid: AMRGrid,
	targetLevel: number,
	poly: ReadonlyArray<Pt>,
	rimNodeIndices: number[],
	rimElevations: number[] | undefined,
	fallbackElev: number,
	useRimElevations: boolean,
	softPinK: number,
	bandWidth: number,
	bandDirection: "both" | "outward",
	cOverride: number | undefined,
	fOverride: number | undefined,
	featDominance: number = 0,
	doFallback: boolean = false,
): void {
	if (rimNodeIndices.length === 0 || bandWidth <= 0) return;
	// `targetLevel` is currently unused beyond establishing the band's uniform refinement
	// (the widened pad in `rasterizeClosedRing` already refined the footprint). Kept for
	// signature symmetry + future per-level rate tuning.
	void targetLevel;
	const cells = grid.cells;
	// `outwardOnly` (soft-plateau case): the BFS refuses cells whose CENTER is inside the
	// polygon (interior cells already carry uniform-k SoftPin from the main loop — the band
	// must NOT overwrite that). All rim seeds (d=0) are accepted unconditionally.
	const outwardOnly = bandDirection === "outward";

	// Per-cell metres-distance from the nearest rim seed, indexed by `cellIndex` (SoA index
	// into `CellArrays`), matching `buildDetailDamping`; Infinity = unreached. Also track the
	// nodeIdx per reached cell (needed for per-vertex elevation interpolation at write).
	const dist = new Float64Array(cells.count).fill(Infinity);
	const nodeForCell = new Int32Array(cells.count).fill(-1);
	// BFS queue of node indices.  Seeded with the rim cells (multi-source).
	// **2026-08-05 — blocked-seed fallback:** probe each seed via `writeSoftPin` (a cell can
	// still be blocked between the sweep and this pass); blocked + `doFallback` → reroute.
	const root = grid.nodes[0];
	const queue: number[] = [];
	const bandRect = {
		minX: root.minX - bandWidth, minY: root.minY - bandWidth,
		maxX: root.maxX + bandWidth, maxY: root.maxY + bandWidth,
	};
	for (const nodeIdx of rimNodeIndices) {
		const ci = grid.nodes[nodeIdx].cellIndex;
		if (ci < 0) continue;
		let elev = fallbackElev;
		if (useRimElevations && rimElevations != null) {
			const n = grid.nodes[nodeIdx];
			const cx = (n.minX + n.maxX) * 0.5;
			const cy = (n.minY + n.maxY) * 0.5;
			elev = sampleElevationAlongPolyline(cx, cy, poly, rimElevations);
		}
		const wrote = writeSoftPin(grid, ci, elev, softPinK, featDominance);
		if (wrote) {
			dist[ci] = 0;
			nodeForCell[ci] = nodeIdx;
			queue.push(nodeIdx);
		} else if (doFallback) {
			// Blocked seed → reroute within the band's reachable set at full k (ramp=1 — the
			// relocated cell is the band's new origin). `withinBand` bounds the search to the
			// feature's band footprint = bbox ± (cell + bandWidth) (the band BFS's reach).
			const fp = bboxOf(poly);
			const pad = cellSizeAtLevel(grid, targetLevel);
			const withinBand = (n2: number): boolean=>{
				const nn = grid.nodes[n2];
				return nn.maxX >= fp.minX - pad - bandWidth && nn.minX <= fp.maxX + pad + bandWidth
					&& nn.maxY >= fp.minY - pad - bandWidth && nn.minY <= fp.maxY + pad + bandWidth;
			};
			fallbackPinInRect(grid, nodeIdx, bandRect, withinBand, targetLevel,
				(ipos, irel)=>{
					const rci = grid.nodes[irel].cellIndex;
					// Re-resolve elevation at the relocated cell (matching the seed loop's
					// interpolation; `ipos` is the relocated world position).
					let rele = fallbackElev;
					if (useRimElevations && rimElevations != null) {
						rele = sampleElevationAlongPolyline(ipos.x, ipos.y, poly, rimElevations);
					}
					writeSoftPin(grid, rci, rele, softPinK, featDominance);
					if (cOverride != null) cells.c[rci] = cOverride;
					if (fOverride != null) cells.f[rci] = fOverride;
					dist[rci] = 0;
					nodeForCell[rci] = irel;
					queue.push(irel);
				});
		}
	}

	// Half-cell size of a node (metres).  Edge weight = halfCell(source) + halfCell(target).
	const halfCell = (nodeIdx: number): number=>{
		const n = grid.nodes[nodeIdx];
		return (n.maxX - n.minX) * 0.5;
	};

	// Neighbour-leaf discovery across AMR levels: sample two points along the shared face
	// (quarter + three-quarter offsets) + `findLeafContaining`, deduplicating. Mirrors
	// `buildDetailDamping`'s `neighborsOf` (kept inline; the BFS visits few cells).
	const neighborsOf = (nodeIdx: number): number[]=>{
		const node = grid.nodes[nodeIdx];
		const sz = node.maxX - node.minX;
		const eps = sz * 1e-3;
		const out: number[] = [];
		const offsets = sz === 0 ? [0.5] : [0.25, 0.75];
		for (let dir = 0; dir < 4; dir++) {
			const dx = FACE_DX[dir], dy = FACE_DY[dir];
			for (const off of offsets) {
				let tx: number, ty: number;
				if (dx !== 0) { tx = (dx > 0 ? node.maxX : node.minX) + dx * eps; ty = node.minY + sz * off; }
				else          { tx = node.minX + sz * off; ty = (dy > 0 ? node.maxY : node.minY) + dy * eps; }
				if (tx < root.minX || tx > root.maxX || ty < root.minY || ty > root.maxY) continue;
				const nb = findLeafContaining(grid, tx, ty);
				if (nb !== nodeIdx && !out.includes(nb)) out.push(nb);
			}
		}
		return out;
	};

	// BFS — Dijkstra-style with metres edge weights (uniform-level footprint → plain FIFO
	// suffices). **`outwardOnly` gate (2026-07-31):** "outward" refuses neighbours whose
	// CENTER is inside the polygon — interior uniform-k SoftPin must not be overwritten.
	while (queue.length > 0) {
		const nodeIdx = queue.shift()!;
		const ci = grid.nodes[nodeIdx].cellIndex;
		const d = dist[ci];
		// Expand all 4 neighbours.
		for (const nb of neighborsOf(nodeIdx)) {
			const nci = grid.nodes[nb].cellIndex;
			if (nci < 0) continue;
			// Outward-only gate: refuse interior cells (center inside the polygon) — the band
			// stays on the exterior side of the rim. NB: rim seeds (d=0) were queued before
			// this gate, so it only filters their NEIGHBOURS.
			if (outwardOnly) {
				const nbNode = grid.nodes[nb];
				const nbx = (nbNode.minX + nbNode.maxX) * 0.5;
				const nby = (nbNode.minY + nbNode.maxY) * 0.5;
				if (pointInPolygon(nbx, nby, poly)) continue;
			}
			// No level check needed here: `forceUniformLevelWorkingGrid` refines the entire root
			// to `targetLevel` (2026-08-02 uniform-refine fix in `solveWorkingGrid`), so no
			// coarser fringe leaves exist; the uniformity assertion there catches regressions.
			const edge = halfCell(nodeIdx) + halfCell(nb);
			const nd = d + edge;
			if (nd < dist[nci] && nd <= bandWidth) {
				dist[nci] = nd;
				nodeForCell[nci] = nb;
				queue.push(nb);
			}
		}
	}

	// Write SoftPin to every reached cell with the cosine ramp `cos(π·d/(2W))` (1 at d=0 → 0
	// at d=W): the rim keeps the full user-facing k ("guider, not a clamp"), the taper makes
	// the change smooth over W; pull grows with W (lower `softPinStiffness` when widening).
	const halfPiOverW = Math.PI * 0.5 / bandWidth;
	for (let ci = 0; ci < cells.count; ci++) {
		const d = dist[ci];
		if (!isFinite(d)) continue;
		// Clamp d into [0, W] for the ramp (defensive — the BFS guard `nd <= bandWidth` already
		// bounds it, but FP rounding at the band edge could push `d` a hair over W).
		const dc = d > bandWidth ? bandWidth : (d < 0 ? 0 : d);
		const ramp = Math.cos(dc * halfPiOverW);
		// `ramp` ∈ [0, 1]. Skip cells whose ramp rounds to ~0 (the band edge) — a zero-
		// stiffness SoftPin is a no-op and would bloat the `CellType.SoftPin` count + the
		// workingGridFingerprint hash.
		const stiffness = softPinK * ramp;
		if (stiffness <= 0) continue;
		// Resolve this cell's target elevation. Band cells are offset from the polyline, so
		// `sampleElevationAlongPolyline` finds the nearest contour point + interpolates — a
		// smooth extension of the per-vertex rim heights into the band; without `rimElevations`, use the fallback scalar.
		let elev = fallbackElev;
		if (useRimElevations && rimElevations != null) {
			const n = nodeForCell[ci];
			if (n >= 0) {
				const cx = (grid.nodes[n].minX + grid.nodes[n].maxX) * 0.5;
				const cy = (grid.nodes[n].minY + grid.nodes[n].maxY) * 0.5;
				elev = sampleElevationAlongPolyline(cx, cy, poly, rimElevations);
			}
		}
		writeSoftPin(grid, ci, elev, stiffness, featDominance);
		if (cOverride != null) cells.c[ci] = cOverride;
		if (fOverride != null) cells.f[ci] = fOverride;
	}
}

/** §6.3 — Open-curve Dirichlet rasterization. Refines a tube around the polyline to
 *  `cellSize`, marks cells the polyline passes through as Dirichlet at the interpolated
 *  elevation, and optionally pins Neumann on the two side-faces. */
function rasterizeOpenCurve(grid: AMRGrid, feat: OpenCurveFeature, cfg: PoissonSolverConfig, stats: RasterizeResult): void {
	if (feat.polyline.length === 0) return;
	const targetCellSize = feat.cellSize ?? cfg.defaultConstraintCellSize;
	const targetLevel = levelForCellSize(grid.rootSize, targetCellSize, grid.maxDepth);
	const cellSz = cellSizeAtLevel(grid, targetLevel);
	const poly = feat.polyline;

	// **2026-08-04 — `pinType: "soft"` on an open path (was closed-ring-only).** `feat.softPin`
	// → channel cells become SoftPin (penalty `k·(H − target)`); `softPinBandWidth > 0` spreads
	// it across a symmetric tube on BOTH sides (→ always "both"). Not honoured with `noFlux`.
	const doSoftPin = feat.softPin === true;
	const softPinBandWidth = doSoftPin ? (feat.softPinBandWidth ?? 0) : 0;
	const doBand = softPinBandWidth > 0;
	// Compute the pre-scaled k (caller scales the user-facing `softPinStiffness` by 1/Δ² so
	// k=1 ≈ one Laplacian neighbour's diagonal contribution at any cell size — the same
	// convention as `rasterizeClosedRing`).
	const softPinDelta = cellSz;
	const softPinInvDelta2 = 1 / (softPinDelta * softPinDelta);
	const softPinK = (feat.softPinStiffness ?? 1) * softPinInvDelta2;

	// 1) Refine a tube around the polyline: union of per-segment bboxes padded by one cell
	//    (or by `bandWidth + one cell` in band mode — same widening as `rasterizeClosedRing`,
	//    so the band cells exist at `targetLevel` for the BFS).
	const bb = bboxOf(poly);
	const refinePad = cellSz + (doBand ? softPinBandWidth : 0);
	const tubeMinX = bb.minX - refinePad, tubeMinY = bb.minY - refinePad;
	const tubeMaxX = bb.maxX + refinePad, tubeMaxY = bb.maxY + refinePad;
	refineLeavesIntersectingRect(grid, tubeMinX, tubeMinY, tubeMaxX, tubeMaxY, targetLevel);
	const tubeRect = {minX: tubeMinX, minY: tubeMinY, maxX: tubeMaxX, maxY: tubeMaxY};

	// **2026-08-02 — pin dominance + fallback.**  See `PathTerrainFeature.dominance` /
	//  `fallbackPin`.  Open-curve fallback searches within the refined tube bbox.
	const featDominance = feat.dominance ?? 0;
	const doFallback = feat.fallbackPin === true;

	// Collect channel cell node indices during the leaves sweep so the band pass can use
	// them as BFS seeds (only meaningful when `doSoftPin && doBand`; collected
	// unconditionally is cheaper than a second pass to re-find the channel).
	const channelNodeIndices: number[] = [];

	// Special-case a single-vertex polyline (degenerate segment): pin cells whose AABB contains
	// that vertex to the single elevation. This makes spot-shaped open features work without a
	// separate code path.
	if (poly.length === 1) {
		const p = poly[0];
		const e0 = feat.elevations[0] ?? 0;
		leavesInRect(grid, tubeRect.minX, tubeRect.minY, tubeRect.maxX, tubeRect.maxY, (nodeIdx)=>{
			const node = grid.nodes[nodeIdx];
			if (node.level < targetLevel) return;
			if (p.x >= node.minX && p.x <= node.maxX && p.y >= node.minY && p.y <= node.maxY) {
				// **2026-08-04 — soft-pin (single-vertex open path).** When `doSoftPin`, the
				// spot-shaped channel cell becomes SoftPin at full k (no contour to band-spread
				// along — band mode is degenerate here). Falls back to Dirichlet otherwise.
				if (doSoftPin) {
					const wrote = writeSoftPin(grid, node.cellIndex, e0, softPinK, featDominance);
					if (!wrote && doFallback) {
						fallbackPinInRect(grid, nodeIdx, tubeRect, undefined, targetLevel,
							(ipos, irel)=>writeSoftPin(grid, grid.nodes[irel].cellIndex, e0, softPinK, featDominance));
					}
				} else {
					const wrote = writeDirichlet(grid, node.cellIndex, e0, stats, featDominance);
					if (!wrote && doFallback) {
						fallbackPinInRect(grid, nodeIdx, tubeRect, undefined, targetLevel,
							(ipos, irel)=>writeDirichlet(grid, grid.nodes[irel].cellIndex, e0, stats, featDominance));
					}
				}
			}
		});
		return;
	}

	const doNeumann = cfg.enableNeumann && feat.crossingSlope != null;
	leavesInRect(grid, tubeRect.minX, tubeRect.minY, tubeRect.maxX, tubeRect.maxY, (nodeIdx)=>{
		const node = grid.nodes[nodeIdx];
		if (node.level < targetLevel) return;
		const cellIdx = node.cellIndex;
		// Test any segment intersects this cell's AABB.
		let hit = false;
		for (let i = 0; i < poly.length - 1; i++) {
			if (segmentIntersectsAABB(poly[i].x, poly[i].y, poly[i + 1].x, poly[i + 1].y,
				node.minX, node.minY, node.maxX, node.maxY)) {
				hit = true; break;
			}
		}
		if (!hit) return;
		// Elevation at this cell ≈ elevation of the nearest tessellated point.
		const cx = (node.minX + node.maxX) * 0.5;
		const cy = (node.minY + node.maxY) * 0.5;
		// noFlux (open-curve barrier): the cells become Barrier instead of Dirichlet/SoftPin
		// (elevation unused for Barrier cells). writeBarrier first so the prior-Barrier
		// short-circuit fires cleanly (with noFlux the bridge doesn't forward softPin*).
		if (feat.noFlux) writeBarrier(grid, cellIdx);
		const elev = sampleElevationAlongPolyline(cx, cy, poly, feat.elevations);
		// **2026-08-04 — `pinType: "soft"` open-path channel write.** `doSoftPin` → channel
		// becomes SoftPin (penalty `k·(H − target)`). `doBand` → band pass is the authoritative
		// channel writer (seed only, ramp=1 at d=0); else 1-cell pin. Falls back to Dirichlet when `!doSoftPin`.
		if (doSoftPin) {
			if (doBand) {
				// Band mode: the band pass writes the channel cells at ramp=1 (full k).  Only
				// collect the seed here.

				// **2026-08-05 — band + fallback: probe each seed with a full-k write (idempotent
				// with the band's ramp-1 re-write); blocked → reroute the seed within the tube.
				const wroteSeed = writeSoftPin(grid, cellIdx, elev, softPinK, featDominance);
				if (wroteSeed) {
					channelNodeIndices.push(nodeIdx);
				} else if (doFallback) {
					// Blocked channel seed → reroute the band source within the refined tube.
					fallbackPinInRect(grid, nodeIdx, tubeRect, undefined, targetLevel,
						(ipos, irel)=>channelNodeIndices.push(irel));
				}
			} else {
				// 1-cell soft pin (no band).  Write the channel cell at the full k.
				const wrote = writeSoftPin(grid, cellIdx, elev, softPinK, featDominance);
				if (!wrote && doFallback) {
					fallbackPinInRect(grid, nodeIdx, tubeRect, undefined, targetLevel,
						(ipos, irel)=>writeSoftPin(grid, grid.nodes[irel].cellIndex,
							sampleElevationAlongPolyline(ipos.x, ipos.y, poly, feat.elevations),
							softPinK, featDominance));
				}
			}
		} else {
			const wrote = writeDirichlet(grid, cellIdx, elev, stats, featDominance);
			if (!wrote && doFallback) {
				fallbackPinInRect(grid, nodeIdx, tubeRect, undefined, targetLevel,
					(ipos, irel)=>writeDirichlet(grid, grid.nodes[irel].cellIndex,
						sampleElevationAlongPolyline(ipos.x, ipos.y, poly, feat.elevations),
						stats, featDominance));
			}
		}

		// Neumann side-faces (§6.3): faces ⊥ local tangent. Sign watchpoint (poisson-v3.md): SAME
		// +g0 both sides → ∂H/∂n_outward = +g0 each (NEU_SIGN flips the axis; ±g0 = one-sided wall).
		// 2026-08-05: `crossingSlope` is a shared scalar (no per-vertex arrays) — same g0 on every cell.
		if (doNeumann) {
			const tang = localTangent(cx, cy, poly);
			const sideFaces = sideFacesForTangent(tang);
			const g0 = feat.crossingSlope ?? 0;
			// Same sign on both side-faces: symmetric valley walls (NEU_SIGN handles the axis flip).
			writeNeumannFace(grid, cellIdx, sideFaces[0], +g0);
			writeNeumannFace(grid, cellIdx, sideFaces[1], +g0);
		}
	});

	// 2026-08-04 — `softPinBandWidth` band-spread pass: multi-source BFS from channel seeds
	// outward ≤ bandWidth m on both sides (open path → symmetric; k·cos(π·d/(2W)), 1 at channel).
	// Always "both" (outwardOnly gate inactive); `poly` for interpolation; `feat.elevations` = rim-elevations.
	const useRimElevations = feat.elevations != null && feat.elevations.length === poly.length;
	const fallbackElev = useRimElevations && feat.elevations.length > 0 ? feat.elevations[0] : 0;
	if (doSoftPin && doBand) {
		spreadSoftPinBand(
			grid, targetLevel, poly,
			channelNodeIndices, feat.elevations, fallbackElev, useRimElevations,
			softPinK, softPinBandWidth,
			"both", // always "both" — an open polyline has no inside/outside (see comment above)
			undefined, undefined, // cOverride/fOverride not yet forwarded to OpenCurveFeature (TODO; closed-ring forwards them).
			featDominance,
			doFallback, // 2026-08-05 — reroute seeds still blocked at band-write time
		);
	}
}

/** §6.4 — Cliff rasterization. Refines a tube along the cliff line; for `noFlux=true` marks
 *  the cells the polyline passes through as Barrier; for `noFlux=false` just refines (the
 *  cells stay Free and the solver produces a steep-but-continuous transition). */
function rasterizeCliff(grid: AMRGrid, feat: CliffFeature, cfg: PoissonSolverConfig, _stats: RasterizeResult): void {
	if (!cfg.enableBarriers && feat.noFlux) return; // barriers disabled → treat as nothing
	if (feat.polyline.length === 0) return;
	const targetCellSize = feat.cellSize ?? cfg.defaultConstraintCellSize;
	const targetLevel = levelForCellSize(grid.rootSize, targetCellSize, grid.maxDepth);
	const cellSz = cellSizeAtLevel(grid, targetLevel);

	const bb = bboxOf(feat.polyline);
	refineLeavesIntersectingRect(
		grid, bb.minX - cellSz, bb.minY - cellSz, bb.maxX + cellSz, bb.maxY + cellSz, targetLevel,
	);

	if (!feat.noFlux) return; // steep-transition: refinement only, cells stay Free

	const poly = feat.polyline;
	leavesInRect(grid, bb.minX - cellSz, bb.minY - cellSz, bb.maxX + cellSz, bb.maxY + cellSz, (nodeIdx)=>{
		const node = grid.nodes[nodeIdx];
		if (node.level < targetLevel) return;
		for (let i = 0; i < poly.length - 1; i++) {
			if (segmentIntersectsAABB(poly[i].x, poly[i].y, poly[i + 1].x, poly[i + 1].y,
				node.minX, node.minY, node.maxX, node.maxY)) {
				writeBarrier(grid, node.cellIndex);
				return;
			}
		}
	});
}

/** §6.6 — Spot-height rasterization. Refines a disk around the anchor to `cellSize` and
 *  marks cells whose centers fall within `radius` as Dirichlet at `elevation`. */
function rasterizeSpotHeight(grid: AMRGrid, feat: SpotHeightFeature, cfg: PoissonSolverConfig, stats: RasterizeResult): void {
	const targetCellSize = feat.cellSize ?? cfg.defaultConstraintCellSize;
	const targetLevel = levelForCellSize(grid.rootSize, targetCellSize, grid.maxDepth);
	const r = Math.max(feat.radius, cellSizeAtLevel(grid, targetLevel) * 0.5);
	// Refine the disk's bbox to target level.
	const diskMinX = feat.x - r, diskMinY = feat.y - r, diskMaxX = feat.x + r, diskMaxY = feat.y + r;
	refineLeavesIntersectingRect(grid, diskMinX, diskMinY, diskMaxX, diskMaxY, targetLevel);
	// **2026-08-02 — pin dominance + fallback.**  See `PathTerrainFeature.dominance` /
	//  `fallbackPin`.  Spot-height fallback searches within the disk (gated by `radius`).
	const featDominance = feat.dominance ?? 0;
	const doFallback = feat.fallbackPin === true;
	const diskRect = {minX: diskMinX, minY: diskMinY, maxX: diskMaxX, maxY: diskMaxY};
	const withinDisk = (nodeIdx: number): boolean=>{
		const n = grid.nodes[nodeIdx];
		const cx = (n.minX + n.maxX) * 0.5, cy = (n.minY + n.maxY) * 0.5;
		const dx = cx - feat.x, dy = cy - feat.y;
		return dx * dx + dy * dy <= r * r;
	};
	// Pin cells whose centers are within radius.
	leavesInRect(grid, diskMinX, diskMinY, diskMaxX, diskMaxY, (nodeIdx)=>{
		const node = grid.nodes[nodeIdx];
		if (node.level < targetLevel) return;
		if (!withinDisk(nodeIdx)) return;
		const wrote = writeDirichlet(grid, node.cellIndex, feat.elevation, stats, featDominance);
		if (!wrote && doFallback) {
			fallbackPinInRect(grid, nodeIdx, diskRect, withinDisk, targetLevel,
				(_ipos, irel)=>writeDirichlet(grid, grid.nodes[irel].cellIndex, feat.elevation, stats, featDominance));
		}
	});
}

// ============================================================
// Outer boundary cells (§3.4)
// ============================================================

/** Mark outer-boundary leaves with the configured outer BC: `flat` = Dirichlet at
 *  `outerFlatElevation`; `neumann_zero` = no-op (out-of-world faces already Neumann-zero);
 *  `ambient` = Dirichlet at `ambientHeight` (full `L(p)` is Phase 6; Phase 1/2 default flat-0). */
export function applyOuterBC(
	grid: AMRGrid,
	cfg: PoissonSolverConfig,
	ambientHeight?: (x: number, y: number)=>number,
	stats?: RasterizeResult,
): void {
	if (cfg.outerBC === "neumann_zero") return;
	const root = grid.nodes[0];
	const valueFor = (x: number, y: number): number=>{
		if (cfg.outerBC === "flat") return cfg.outerFlatElevation;
		if (cfg.outerBC === "ambient") return ambientHeight != null ? ambientHeight(x, y) : cfg.outerFlatElevation;
		return cfg.outerFlatElevation;
	};
	leavesInRect(grid, root.minX, root.minY, root.maxX, root.maxY, (nodeIdx)=>{
		const n = grid.nodes[nodeIdx];
		const onEdge = n.minX <= root.minX + 1e-9 || n.maxX >= root.maxX - 1e-9
			|| n.minY <= root.minY + 1e-9 || n.maxY >= root.maxY - 1e-9;
		if (!onEdge) return;
		const cellIdx = n.cellIndex;
		if (grid.cells.type[cellIdx] === CellType.Barrier) return; // barrier wins even at edge
		// **2026-07-30 — SoftPin cells at the world edge keep their soft pin** (a soft-pinned
		// feature near the world edge shouldn't be coerced to the flat outer-BC value; the
		// penalty is the user's intent, the outer-BC is the global fallback).
		if (grid.cells.type[cellIdx] === CellType.SoftPin) return;
		const cx = (n.minX + n.maxX) * 0.5;
		const cy = (n.minY + n.maxY) * 0.5;
		grid.cells.type[cellIdx] = CellType.Dirichlet;
		grid.cells.bcValue[cellIdx] = valueFor(cx, cy);
		// 2026-08-02 — outer-BC writes `dominance = Infinity` so feature pins can't overwrite the
		// world-edge BC (global fallback; preserves prior "outer-BC always wins at edge" behaviour —
		// features reaching the edge should use noFlux/pinShape:"none" instead of dominance).
		grid.cells.dominance[cellIdx] = Infinity;
		if (stats) stats.counts.outer++;
	});
}

// ============================================================
// Isoline nesting (Dirichlet-conflict ordering helper)
// ============================================================

/** Ring's bounding box (a cached form of {@link bboxOf} for the nesting pass — avoids
 *  recomputing for every pair-test since `digestIsClosedRing` runs over N² ring pairs. */
function ringBBox(poly: ReadonlyArray<Pt>): {minX: number; minY: number; maxX: number; maxY: number} {
	return bboxOf(poly);
}

/** Quick AABB containment (epsilon margin): coarse pre-filter before the per-point
 *  `pointInPolygon` test — prunes ~all non-nested ring pairs without the ray-caster. */
function bboxInside(
	inner: {minX: number; minY: number; maxX: number; maxY: number},
	outer: {minX: number; minY: number; maxX: number; maxY: number},
	eps = 1e-9,
): boolean {
	return inner.minX >= outer.minX - eps && inner.minY >= outer.minY - eps
		&& inner.maxX <= outer.maxX + eps && inner.maxY <= outer.maxY + eps;
}

/** Whether ring `innerPoly` is geometrically inside `outerPoly`: every inner vertex must lie
 *  inside outer (all vertices, not just one, so a "peanut" outer wrapping two peaks stays a
 *  true container; partly-overlapping siblings each have an outside vertex → neither nested). */
function ringIsInside(innerPoly: ReadonlyArray<Pt>, outerPoly: ReadonlyArray<Pt>): boolean {
	for (const p of innerPoly) {
		if (!pointInPolygon(p.x, p.y, outerPoly)) return false;
	}
	return true;
}

/** Nesting depth per closed-ring feature (0 = in no ring; 1 = in exactly one, …; DFS over
 *  the containment chain: depth[X] = 1 + max depth of any ring containing X). Used to order
 *  Dirichlet writes outermost-first so inner pins win (V3 analogue of V2 fix b727f7d8). */
function closedRingNestingDepths(features: ReadonlyArray<RasterFeature>): Int32Array {
	const ringIdxs: number[] = [];
	for (let i = 0; i < features.length; i++) {
		if (features[i].kind === "closedRing") ringIdxs.push(i);
	}
	const depths = new Int32Array(features.length); // depth 0 default for non-rings
	if (ringIdxs.length <= 1) return depths;

	// Precompute bboxes once.
	const bboxes = new Map<number, {minX: number; minY: number; maxX: number; maxY: number}>();
	const polys = new Map<number, Pt[]>();
	for (const i of ringIdxs) {
		bboxes.set(i, ringBBox((features[i] as ClosedRingFeature).polyline));
		polys.set(i, (features[i] as ClosedRingFeature).polyline);
	}

	// Find each ring's immediate parent = the deepest ring that directly contains it (no
	// intermediate ring). Depths aren't computed yet, so track the SMALLEST-area container as
	// the immediate parent — keeps deep nesting a chain; the max-depth tie-break is below.
	const parent = new Int32Array(features.length).fill(-1);
	for (const i of ringIdxs) {
		let bestParent = -1;
		let bestParentDepth = -1;
		const iBB = bboxes.get(i)!;
		const iPoly = polys.get(i)!;
		for (const j of ringIdxs) {
			if (j === i) continue;
			const jBB = bboxes.get(j)!;
			// AABB pre-filter: only run the all-vertices test if i's bbox fits inside j's.
			if (!bboxInside(iBB, jBB)) continue;
			if (!ringIsInside(iPoly, polys.get(j)!)) continue;
			if (bestParent < 0) {
				bestParent = j;
			} else {
				// Prefer the SMALLER-area container as immediate parent (the directly-containing
				// ring is the smallest one that still contains i) so deep nesting produces a chain
				// of immediate parents rather than skipping straight to the outermost.
				const candBB = bboxes.get(bestParent)!;
				const candArea = (candBB.maxX - candBB.minX) * (candBB.maxY - candBB.minY);
				const jArea = (jBB.maxX - jBB.minX) * (jBB.maxY - jBB.minY);
				if (jArea < candArea) bestParent = j;
			}
		}
		parent[i] = bestParent;
	}

	// Resolve depth[i] = 1 + depth[parent[i]] (with memoisation; -1 parent → 0).
	const memo = new Map<number, number>();
	const depthOf = (i: number): number=>{
		if (memo.has(i)) return memo.get(i)!;
		const p = parent[i];
		const d = p < 0 ? 0 : 1 + depthOf(p);
		memo.set(i, d);
		depths[i] = d;
		return d;
	};
	for (const i of ringIdxs) depthOf(i);
	return depths;
}

// ============================================================
// Main entry point
// ============================================================

/** Rasterize all features onto `grid` (in place) in precedence order, then apply the outer BC.
 *  Barriers first, then Dirichlet — closed rings outermost-first by nesting depth so inner pins
 *  win (V3 analogue of V2 fix b727f7d8; 2026-07-28 "peanut clobbers peaks"); others keep order. */
export async function rasterizeConstraints(
	grid: AMRGrid,
	features: ReadonlyArray<RasterFeature>,
	cfg: PoissonSolverConfig,
	ambientHeight?: (x: number, y: number)=>number,
	/** 2026-08-03 rapid-zoom-queue fix — cooperative-cancellation handle threaded into
	 *  `rasterizeClosedRing`'s per-leaf loop (throttled `await signal.maybeYield()`; the
	 *  200ms+ hot path). Omitted in test lanes → synchronous, byte-identical output. */
	signal?: VetoSignal,
): Promise<RasterizeResult> {
	const stats: RasterizeResult = {
		counts: {free: 0, dirichlet: 0, neumann: 0, barrier: 0, outer: 0},
		dirichletConflicts: 0,
		maxLevelReached: 0,
	};

	// Barriers first (so later Dirichlet writes skip barrier cells via writeDirichlet's
	// prior-Barrier short-circuit). Covers CliffFeature noFlux=true; closed-ring noFlux
	// features write their own barrier inline in rasterizeClosedRing (unconditional, safe).
	for (const f of features) {
		if (f.kind === "cliff" && f.noFlux) rasterizeCliff(grid, f, cfg, stats);
	}
	// Dirichlet features: order closed rings by nesting depth (outermost first → inner wins);
	// open curves, spot heights, steep cliffs keep their array order (no nesting hierarchy).
	const ringDepths = closedRingNestingDepths(features);
	const dirichletOrder = Array.from({length: features.length}, (_, i)=>i).sort((a, b)=>{
		const aRing = features[a].kind === "closedRing";
		const bRing = features[b].kind === "closedRing";
		// Rings first (sorted by nesting depth ascending → outermost rings apply first).
		if (aRing && !bRing) return -1;
		if (!aRing && bRing) return 1;
		if (aRing && bRing) return ringDepths[a] - ringDepths[b];
		return a - b; // both non-ring: preserve original array order (stable for same-kind)
	});
	for (const i of dirichletOrder) {
		// 2026-08-03 rapid-zoom-queue fix — per-feature yield checkpoint: rasterizeClosedRing's
		// per-leaf loop is the 200ms+ hot path; this covers the inter-feature gap. Throttle
		// (~100ms) → at most one setTimeout(0) per feature.
		if (signal && isVetoSignal(signal)) {
			if (signal.aborted) throw new SolveAbortedError();
			await signal.maybeYield();
			if (signal.aborted) throw new SolveAbortedError();
		}
		const f = features[i];
		switch (f.kind) {
			case "closedRing": await rasterizeClosedRing(grid, f, cfg, stats, signal); break;
			case "openCurve": rasterizeOpenCurve(grid, f, cfg, stats); break;
			case "spotHeight": rasterizeSpotHeight(grid, f, cfg, stats); break;
			case "cliff": if (!f.noFlux) rasterizeCliff(grid, f, cfg, stats); break;
		}
	}

	// Outer BC applied last so it doesn't override interior constraints (it only writes cells
	// touching the world root edge, which features rarely reach).
	applyOuterBC(grid, cfg, ambientHeight, stats);

	// Tally cell-type counts + deepest leaf.
	for (let i = 0; i < grid.cells.count; i++) {
		switch (grid.cells.type[i] as CellType) {
			case CellType.Free: stats.counts.free++; break;
			case CellType.Dirichlet: stats.counts.dirichlet++; break;
			case CellType.Neumann: stats.counts.neumann++; break;
			case CellType.Barrier: stats.counts.barrier++; break;
		}
	}
	for (let i = 0; i < grid.nodes.length; i++) {
		const n = grid.nodes[i];
		if (n.firstChild < 0 && n.level > stats.maxLevelReached) stats.maxLevelReached = n.level;
	}
	return stats;
}

// ============================================================
// Small geometry utilities used by the closed-ring / open-curve rasterizers
// ============================================================

/** For a rim cell of a closed ring, determine which face (E/N/W/S) best points "outward"
 *  (away from the ring interior). Returns -1 if no clear outward face. */
function dominantOutwardFace(grid: AMRGrid, node: QuadNode, poly: ReadonlyArray<Pt>): number {
	const cx = (node.minX + node.maxX) * 0.5;
	const cy = (node.minY + node.maxY) * 0.5;
	// Find the nearest segment and its outward normal (pointing away from polygon centroid).
	const centroid = polygonCentroid(poly);
	let bestI = 0, bestDist = Infinity;
	for (let i = 0; i < poly.length; i++) {
		const d = (poly[i].x - cx) ** 2 + (poly[i].y - cy) ** 2;
		if (d < bestDist) { bestDist = d; bestI = i; }
	}
	const a = poly[bestI];
	const b = poly[(bestI + 1) % poly.length];
	const tx = b.x - a.x, ty = b.y - a.y;
	// Normal = perpendicular to tangent, pointing away from centroid.
	let nx = -ty, ny = tx;
	const midx = (a.x + b.x) * 0.5, midy = (a.y + b.y) * 0.5;
	if ((midx - centroid.x) * nx + (midy - centroid.y) * ny < 0) { nx = -nx; ny = -ny; }
	// Pick the dominant axis as the face.
	if (Math.abs(nx) >= Math.abs(ny)) {
		return nx >= 0 ? Face.E : Face.W;
	} else {
		return ny >= 0 ? Face.N : Face.S;
	}
}

/** Polygon centroid (average of vertices — good enough for normal-direction disambiguation). */
function polygonCentroid(poly: ReadonlyArray<Pt>): Pt {
	let sx = 0, sy = 0;
	for (const p of poly) { sx += p.x; sy += p.y; }
	return {x: sx / poly.length, y: sy / poly.length};
}

/** Sample elevation along an open polyline at world point (x, y): find the nearest point on
 *  the polyline and linearly interpolate the elevation between the surrounding vertices. */
function sampleElevationAlongPolyline(
	x: number, y: number,
	poly: ReadonlyArray<Pt>, elevations: ReadonlyArray<number>,
): number {
	if (poly.length === 0) return 0;
	if (poly.length === 1) return elevations[0] ?? 0;
	// Find the closest segment, project, and interpolate.
	let bestI = 0, bestT = 0, bestD = Infinity;
	for (let i = 0; i < poly.length - 1; i++) {
		const ax = poly[i].x, ay = poly[i].y, bx = poly[i + 1].x, by = poly[i + 1].y;
		const dx = bx - ax, dy = by - ay;
		const lenSq = dx * dx + dy * dy;
		let t = lenSq > 0 ? ((x - ax) * dx + (y - ay) * dy) / lenSq : 0;
		t = Math.max(0, Math.min(1, t));
		const px = ax + t * dx, py = ay + t * dy;
		const d = (px - x) ** 2 + (py - y) ** 2;
		if (d < bestD) { bestD = d; bestI = i; bestT = t; }
	}
	const e0 = elevations[bestI] ?? 0;
	const e1 = elevations[bestI + 1] ?? e0;
	return e0 + (e1 - e0) * bestT;
}

/** Local tangent direction at the nearest point on the polyline. Returns a unit vector. */
function localTangent(x: number, y: number, poly: ReadonlyArray<Pt>): {x: number; y: number} {
	if (poly.length < 2) return {x: 1, y: 0};
	// Nearest segment.
	let bestI = 0, bestD = Infinity;
	for (let i = 0; i < poly.length - 1; i++) {
		const d = distPointToSegment(x, y, poly[i].x, poly[i].y, poly[i + 1].x, poly[i + 1].y);
		if (d < bestD) { bestD = d; bestI = i; }
	}
	const dx = poly[bestI + 1].x - poly[bestI].x;
	const dy = poly[bestI + 1].y - poly[bestI].y;
	const len = Math.hypot(dx, dy) || 1;
	return {x: dx / len, y: dy / len};
}

/** The two side-faces (perpendicular to the curve tangent) for Neumann pinning.
 *  For a tangent direction (tx, ty), the side normal is (-ty, tx); pick the two dominant-axis
 *  faces along that side normal. Returns [facePos, faceNeg] (the +n and −n faces). */
function sideFacesForTangent(t: {x: number; y: number}): [Face, Face] {
	// Side normal.
	const nx = -t.y, ny = t.x;
	if (Math.abs(nx) >= Math.abs(ny)) {
		return nx >= 0 ? [Face.E, Face.W] : [Face.W, Face.E];
	} else {
		return ny >= 0 ? [Face.N, Face.S] : [Face.S, Face.N];
	}
}

// ============================================================
// MapObject → RasterFeature bridge (collectRasterFeatures)
// ============================================================

/** Resolve an elevation Interval to a number (placeholder Module B — see file docblock). */
function resolveElevation(iv: {min?: number; max?: number} | undefined): number {
	if (iv == null) return 0;
	if (iv.min != null && iv.max != null) return (iv.min + iv.max) * 0.5;
	if (iv.min != null) return iv.min;
	if (iv.max != null) return iv.max;
	return 0;
}

/** Resolve a crossingSlope Interval to a scalar (midpoint when both bounds set; one bound
 *  if the other is absent). Undefined → no Neumann. Per-vertex-different slopes are not
 *  supported; a bare `number` is NOT a valid `PathTerrainFeature.crossingSlope` shape. */
function resolveSlope(iv: {min?: number; max?: number} | undefined): number | undefined {
	if (iv == null) return undefined;
	if (iv.min != null && iv.max != null) return (iv.min + iv.max) * 0.5;
	if (iv.min != null) return iv.min;
	if (iv.max != null) return iv.max;
	return undefined;
}

/** Normalize a path point ({x,y} or [x,y] tuple) to {x,y}. */
function normPt(p: {x: number; y: number} | [number, number]): Pt {
	return Array.isArray(p) ? {x: p[0], y: p[1]} : {x: p.x, y: p.y};
}

export function collectRasterFeatures(objects: ReadonlyArray<MapObject_ForTerrain>): RasterFeature[] {
	const out: RasterFeature[] = [];
	for (const obj of objects) {
		// Spot-height modifier.
		for (const m of obj.modifiers ?? []) {
			if (!m.enabled && m.enabled !== undefined) continue;
			const sh = m.terrainSpotHeight;
			if (!sh) continue;
			const pos = obj._extras?.resolvedWorldPos ?? {x: 0, y: 0};
			const off = sh.offset;
			out.push({
				kind: "spotHeight",
				x: pos.x + (off?.x ?? 0), y: pos.y + (off?.y ?? 0),
				elevation: sh.elevation ?? 0,
				radius: sh.radius ?? 0.5,
				// **2026-08-02 — dominance/fallbackPin** forwarded from the map-object's
				// spot-height modifier (rare in practice; spot heights rarely overlap, but
				// the fields type-check and forward cleanly for completeness).
				dominance: (sh as any).dominance,
				fallbackPin: (sh as any).fallbackPin,
			});
		}

		// Path features.
		for (const [index, path] of (obj.paths ?? []).entries()) {
			const tf = path.terrainFeature;
			// 2026-08-01: a present-but-disabled feature (disabled === true) is preserved in the
			// persisted data but inert — skip it here too (the bridge in MapObject_ForTerrain.ts
			// already filters via IsActive, but direct callers / tests bypass the bridge).
			if (tf?.disabled) continue;
			// 2026-07-28: "active" iff pinShape !== "none" OR noFlux === true OR explicit
			// `cellSize` (preserves the former role:"cliff"+noFlux:false refine-only case;
			// none+noFlux:false+no cellSize is inactive → skipped, matches former role:"none").
			const pinShape = (tf?.pinShape ?? "none") as "none" | "interior" | "rim";
			const pinType = (tf?.pinType ?? "hard") as "hard" | "soft";
			const noFlux = !!(tf?.noFlux);
			const hasExplicitCellSize = tf?.cellSize != null;
			if (!tf || (pinShape === "none" && !noFlux && !hasExplicitCellSize)) continue;

			const meshes = path._meshSet_world ?? [];
			for (const mesh of meshes) {
				// The resolved closed flag from the origin path (via ResolveInheritedBase →
				// ResolvePathMeshes, forwarded as `_curve_closed_resolved`). AUTHORITATIVE for mesh
				// shape — NOT `path.curve_closed` (raw flag); a mismatch → wrong edge set rasterized.
				const pathClosed = !!path._curve_closed_resolved;

				const closed = pathClosed && (pinShape === "interior" || pinShape === "rim" || noFlux);
				Assert(obj._extras?.worldSeed != null, "World seed must be supplied in obj for constraint-rasterizer.");
				// The mesh is already curve-resolved upstream by the `interpolate` PathTransform
				// (in `ApplyPathTransformsToPoints`), so the solver consumes `_meshSet_world` as-is.
				const tess = mesh;
				// Both pin shapes use the SAME elevation resolution: perVertexElevations if present,
				// else `elevation` fallback (former closedIsoline used scalar; former openDirichlet
				// used per-vertex; now one path lets users pin a few control points on either shape).
				const fallbackElev = resolveElevation(tf.elevation);
				const perVertex = tf.perVertexElevations;
				// An empty array `perVertexElevations: []` means "no per-vertex overrides — use the
				// `elevation` Interval fallback for every vertex" (NOT "elevation 0 everywhere").
				// Only a non-empty array (with at least one entry) activates per-vertex mode.
				const resolvedPerVertex: number[] | undefined = (perVertex != null && perVertex.length > 0)
					? perVertex.map(v=>(v == null ? fallbackElev : v))
					: undefined;
				// Per-tessellated-point elevations along the tessellated polyline (arc-length
				// interpolated from the per-vertex array to match the tessellation).
				const tessElevations: number[] = resolvedPerVertex != null
					? interpolateAlongTessellation(mesh, tess, resolvedPerVertex)
					: tess.map(()=>fallbackElev);

				// Resolve crossingSlope (Interval → scalar; uniform along the whole contour — no
				// per-vertex-different slopes, matching `PathTerrainFeature.crossingSlope: Interval`).
				const resolvedSlope = resolveSlope(tf.crossingSlope);

				// Dispatch by pinShape+pinType+closed+noFlux to the four kinds (each branch annotated
				// below). 2026-08-04: open-path soft was silently dropped before (inert "forced to
				// Hard"); now OpenCurve forwards softPin*. See doSoftPin/spreadSoftPinBand.
				if (closed) {
					// Closed-path → ClosedRing (interior-pin/rim-only/barrier-only/refine-only;
					// pinInterior distinguishes interior+rim from rim-only). none+noFlux → barrier
					// ring; none+!noFlux+cellSize → refine-only (refines inside the ring's bbox).
					if (pinShape === "none" && noFlux) {
						out.push({
							kind: "closedRing",
							polyline: tess,
							elevation: 0, // unused — writeDirichlet is a no-op after writeBarrier
							pinInterior: false,
							noFlux: true,
							cellSize: tf.cellSize,
							// 2026-08-05 — ambient-weight multiplier applies to the inside of a barrier ring too (e.g. silence ambient inside a walled plateau).
							ambientWeightMultiplier: tf.ambientWeightMultiplier,
						});
					} else if (pinShape === "none" && hasExplicitCellSize) {
						// Refine-only closed ring: no skip-pin flag exists, so emit a ClosedRing with
						// pinInterior=false + elevation=0 — bbox refines, rim pins at 0 (known gap; a
						// future refineOnly flag on ClosedRingFeature would do true refine-only).
						out.push({
							kind: "closedRing",
							polyline: tess,
							elevation: 0,
							pinInterior: false,
							cellSize: tf.cellSize,
							// Note: rim cells pin at 0; this is a known semantic gap. A future
							// refineOnly flag on ClosedRingFeature would do true refine-only.
							// 2026-08-05 — refine-only closed rings still bound a polygon, so the ambient multiplier is forwarded.
							ambientWeightMultiplier: tf.ambientWeightMultiplier,
						});
					} else {
						const pinInterior = pinShape === "interior";
						// 2026-07-30/31 — pinType:"soft" (was rim_soft): cells → SoftPin (k·(H−target),
						// gradient continuous), softPin* forwarded. Band: rim+soft → "both", interior+soft
						// → "outward" (an in-ramp would zero inner stiffness, fighting the plateau intent).
						const isSoftPin = pinType === "soft";
						const softPinBandDirection: "both" | "outward" = pinInterior ? "outward" : "both";
						out.push({
							kind: "closedRing",
							polyline: tess,
							elevation: fallbackElev,
							rimElevations: !pinInterior ? tessElevations : undefined,
							pinInterior,
							noFlux: noFlux ? true : undefined,
							softPin: isSoftPin ? true : undefined,
							softPinStiffness: isSoftPin ? tf.softPinStiffness : undefined,
							softPinBandWidth: isSoftPin ? tf.softPinBandWidth : undefined,
							softPinBandDirection: isSoftPin ? softPinBandDirection : undefined,
							cellSize: tf.cellSize,
							regionC: tf.regionC,
							regionF: tf.regionF,
							crossingSlope: resolvedSlope,
							orientation: tf.orientation,
							// 2026-08-02 — dominance/fallbackPin forwarded so the rasterizer can block
							// lower-dominance pins and reroute blocked fallback-enabled pins inward;
							// 2026-08-03 — muteDominanceWhen forwarded (TwoGridCache.setCamera mute-gate).
							dominance: tf.dominance,
							fallbackPin: tf.fallbackPin,
							muteDominanceWhen: tf.muteDominanceWhen,
							// 2026-08-05 — ambient-weight multiplier forwarded so the eval-time sampler can scale the ambient term inside this ring's polygon.
							ambientWeightMultiplier: tf.ambientWeightMultiplier,
						});
					}
				} else {
					// Open-path feature: OpenCurve (pinShape="rim" — elevation pin along the polyline)
					// or Cliff (none+noFlux → barrier wall; none+!noFlux+cellSize → refine-only cliff).
					if (pinShape === "rim") {
						// 2026-08-04 — pinType:"soft" on open path (open branch silently dropped
						// pinType → inert "forced to Hard", no coercion guard): soft → channel cells
						// SoftPin; bandWidth symmetric tube both sides; noFlux+soft keeps barrier.
						const isSoftPin = pinType === "soft" && !noFlux;
						out.push({
							kind: "openCurve",
							polyline: tess,
							elevations: tessElevations,
							cellSize: tf.cellSize,
							crossingSlope: resolvedSlope,
							noFlux: noFlux ? true : undefined,
							// **2026-08-02 — dominance/fallbackPin** forwarded.
							dominance: tf.dominance,
							fallbackPin: tf.fallbackPin,
							// 2026-08-03 — muteDominanceWhen not forwarded (open-curve question);
							// 2026-08-04 — softPin* forwarded when soft && !noFlux; band direction NOT
							// forwarded — no inside/outside, band always "both" (spreadSoftPinBand).
							softPin: isSoftPin ? true : undefined,
							softPinStiffness: isSoftPin ? tf.softPinStiffness : undefined,
							softPinBandWidth: isSoftPin ? tf.softPinBandWidth : undefined,
						});
					} else {
						// pinShape="none" on an open path: noFlux → Cliff noFlux=true (barrier wall);
						// !noFlux+cellSize → Cliff noFlux=false (refine-only, cells stay Free);
						// !noFlux+no cellSize → filtered out above as inactive.
						out.push({
							kind: "cliff",
							polyline: tess,
							cellSize: tf.cellSize,
							noFlux,
						});
					}
				}
			} // for (const rawPts of rawPtsList)
		}
	}
	return out;
}

/** Map per-control-point elevations (or crossingSlopes) to per-tessellated-point values by
 *  arc-length projection onto the control polyline — a naive k/STEPS index-map is WRONG
 *  (centripetal Catmull-Rom bunches points; mis-pins by up to slope×segLen/2, ~75m/40m seg). */
function interpolateAlongTessellation(
	controlPts: ReadonlyArray<Pt>,
	tess: ReadonlyArray<Pt>,
	perVertex: ReadonlyArray<number>,
): number[] {
	if (perVertex.length === 0) throw new Error("interpolateAlongTessellation: perVertex must be non-empty (the bridge should pass undefined — not [] — when no per-vertex overrides are set, so the `elevation` Interval fallback is used)");
	if (tess.length === 0) return [];
	const out: number[] = new Array(tess.length);
	for (let k = 0; k < tess.length; k++) {
		out[k] = sampleElevationAlongPolyline(tess[k].x, tess[k].y, controlPts, perVertex);
	}
	return out;
}
